(() => {
  const globalName = 'LuchettiArtworkShowcase';
  if (window[globalName]) {
    window[globalName].scan();
    return;
  }

  const FACETS = ['medium', 'surface', 'sketchbook'];
  const KNOWN = {
    medium: ['acrylic', 'watercolor', 'gouache', 'ink', 'colored-pencil', 'graphite', 'pastel', 'marker', 'oil', 'mixed-media', 'another-medium'],
    surface: ['hot-press', 'cold-press'],
    sketchbook: ['original-8x8', 'via-5-5', 'atlas-9x12']
  };
  const LABELS = {
    medium: {
      acrylic: 'Acrylic', watercolor: 'Watercolor', gouache: 'Gouache', ink: 'Ink',
      'colored-pencil': 'Colored pencil', graphite: 'Graphite', pastel: 'Pastel', marker: 'Marker', oil: 'Oil',
      'mixed-media': 'Mixed media', 'another-medium': 'Other'
    },
    surface: { 'hot-press': 'Hot press', 'cold-press': 'Cold press' },
    sketchbook: { 'original-8x8': 'Original 8×8″', 'via-5-5': 'Via 5.5″', 'atlas-9x12': 'Atlas 9×12″' }
  };
  // The lighter hint makes clear Medium is optional and takes several picks.
  const GROUP_TITLES = {
    medium: 'Medium<span class="as-label-hint"> <span aria-hidden="true">·</span> pick any</span>',
    surface: 'Surface',
    sketchbook: 'Sketchbook'
  };
  // Surface and Sketchbook are single-choice dropdowns; Medium stays as multi-select pills.
  const DROPDOWNS = { surface: 'All Surfaces', sketchbook: 'All Sketchbooks' };
  // Atlas pieces stay visible under every view but get no filter option, as on the live page.
  const HIDDEN_OPTIONS = { sketchbook: ['atlas-9x12'] };
  // Listed even when no artwork uses them (a dashed, unavailable pill): every form medium and current sketchbook.
  const ALWAYS_SHOWN = { medium: KNOWN.medium, sketchbook: ['original-8x8', 'via-5-5'] };
  const NO_MONTH = 'undated';
  const ICON = {
    close: '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="m4 4 8 8m0-8-8 8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
    sliders: '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M3 5h8M15 5h2M3 15h2M9 15h8M3 10h12" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><circle cx="13" cy="5" r="2" fill="none" stroke="currentColor" stroke-width="1.7"/><circle cx="7" cy="15" r="2" fill="none" stroke="currentColor" stroke-width="1.7"/></svg>',
    up: '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M8 13V3M3.5 7.5 8 3l4.5 4.5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    prev: '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M10 3.5 5.5 8l4.5 4.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    next: '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="m6 3.5 4.5 4.5L6 12.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    instagram: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="3.5" y="3.5" width="17" height="17" rx="5" fill="none" stroke="currentColor" stroke-width="1.8"/><circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" stroke-width="1.8"/><circle cx="17.2" cy="6.8" r="1.2" fill="currentColor"/></svg>'
  };

  const values = (raw) => (raw || '').split(',').map((value) => value.trim()).filter(Boolean);
  const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;
  const monthDate = (key) => new Date(Date.UTC(Number(key.slice(0, 4)), Number(key.slice(5, 7)) - 1, 1));
  const longMonth = new Intl.DateTimeFormat('en', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const headerBottom = () => {
    const header = document.querySelector('.section-header');
    return header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
  };

  // Shows a loading spinner on host until the image has loaded (or failed).
  function watchImage(host, image) {
    if (!host || !image || image.complete) return;
    host.classList.add('is-loading');
    const done = () => host.classList.remove('is-loading');
    image.addEventListener('load', done, { once: true });
    image.addEventListener('error', done, { once: true });
  }

  // A fixed shuffle, the same for every visitor, that keeps each artist's pieces apart where
  // it can: no artist repeats within the four artworks before.
  function spreadShuffle(cards, seedText) {
    let seed = [...seedText].reduce((hash, char) => Math.imul(hash ^ char.charCodeAt(0), 16777619), 2166136261) >>> 0;
    const random = () => {
      seed = (seed + 0x6d2b79f5) >>> 0;
      let value = Math.imul(seed ^ (seed >>> 15), seed | 1);
      value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
      return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
    };
    // Shuffling from id order makes the result depend only on which artworks are in the month.
    const pool = cards.slice().sort((a, b) => a.id.localeCompare(b.id));
    for (let index = pool.length - 1; index > 0; index -= 1) {
      const swap = Math.floor(random() * (index + 1));
      [pool[index], pool[swap]] = [pool[swap], pool[index]];
    }
    const result = [];
    while (pool.length) {
      const recent = new Set(result.slice(-4).map((card) => card.artist.toLowerCase()));
      const next = pool.findIndex((card) => !recent.has(card.artist.toLowerCase()));
      result.push(pool.splice(Math.max(next, 0), 1)[0]);
    }
    return result;
  }

  function readCard(element) {
    const month = element.dataset.filterMonth || '';
    const position = element.dataset.position === '' ? NaN : Number(element.dataset.position);
    const image = element.querySelector('img');
    const width = Number(image?.getAttribute('width'));
    const height = Number(image?.getAttribute('height'));
    const card = {
      element,
      id: element.dataset.artworkId || '',
      artist: (element.dataset.artist || '').trim(),
      month: month || NO_MONTH,
      position: Number.isFinite(position) ? position : Infinity,
      ratio: width > 0 && height > 0 ? height / width : 1,
      medium: values(element.dataset.filterMedium),
      surface: values(element.dataset.filterSurface),
      sketchbook: values(element.dataset.filterSketchbook)
    };
    if (!card.id || (month && !/^\d{4}-(0[1-9]|1[0-2])$/.test(month))
      || card.surface.length > 1 || card.sketchbook.length > 1
      || FACETS.some((facet) => card[facet].some((value) => !KNOWN[facet].includes(value)))) {
      throw new Error('Invalid artwork metadata');
    }
    return card;
  }

  function init(root) {
    if (root.artworkShowcase) return;
    const mount = root.querySelector('[data-as-filters]');
    const galleryEl = root.querySelector('[data-as-gallery]');
    const status = root.querySelector('.as-status');
    const hint = root.querySelector('.as-hint');
    const pagination = root.querySelector('.as-pagination');
    if (!mount || !galleryEl) return;

    const controller = new AbortController();
    const cleanups = [];
    const instance = { destroyed: false };
    instance.destroy = () => {
      instance.destroyed = true;
      controller.abort();
      cleanups.splice(0).forEach((cleanup) => cleanup());
    };
    root.artworkShowcase = instance;
    const listen = (target, type, handler, options) => {
      target.addEventListener(type, handler, options);
      cleanups.push(() => target.removeEventListener(type, handler, options));
    };

    const spotlights = root.querySelector('.as-spotlights');
    if (spotlights && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        spotlights.classList.toggle('is-paused', !entry.isIntersecting);
      });
      observer.observe(spotlights);
      cleanups.push(() => observer.disconnect());
    }

    const sectionId = root.dataset.sectionId;
    const totalPages = Number(root.dataset.pages);
    const currentPage = Number(root.dataset.currentPage);
    const pageParam = root.dataset.pageParam || 'page';
    const cards = new Map();

    function addCards(container) {
      container.querySelectorAll('.as-card').forEach((element) => {
        const card = readCard(element);
        if (!cards.has(card.id)) cards.set(card.id, card);
      });
    }

    async function fetchPage(page) {
      const url = new URL(window.location.href);
      ['medium', 'surface', 'sketchbook', 'month'].forEach((key) => url.searchParams.delete(key));
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set(pageParam, page);
      for (let attempt = 0; attempt < 2; attempt += 1) {
        // Each attempt has its own time limit, so a stalled request is retried instead of
        // ending the whole load; destroying the gallery still cancels it.
        const request = new AbortController();
        const cancel = () => request.abort();
        controller.signal.addEventListener('abort', cancel, { once: true });
        const timeout = window.setTimeout(cancel, 30000);
        try {
          const response = await fetch(url, { signal: request.signal, credentials: 'same-origin' });
          if (response.status !== 200) throw new Error('Gallery request failed');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const wrapper = [...doc.querySelectorAll('[data-artwork-showcase]')]
            .find((candidate) => candidate.dataset.sectionId === sectionId);
          if (!wrapper || Number(wrapper.dataset.currentPage) !== page
            || Number(wrapper.dataset.pages) !== totalPages
            || (wrapper.dataset.pageParam || 'page') !== pageParam) throw new Error('Incomplete gallery response');
          addCards(wrapper);
          return;
        } catch (error) {
          if (attempt === 1 || controller.signal.aborted) throw error;
        } finally {
          window.clearTimeout(timeout);
          controller.signal.removeEventListener('abort', cancel);
        }
      }
    }

    function fail() {
      if (instance.destroyed) return;
      mount.replaceChildren();
      root.classList.remove('is-ready');
      root.classList.add('is-fallback');
      root.removeAttribute('aria-busy');
      if (status) {
        status.hidden = false;
        status.textContent = `The full gallery could not load. Showing ${galleryEl.querySelectorAll('.as-card').length} artworks; use the page links below.`;
      }
      if (pagination) pagination.hidden = false;
    }

    async function load() {
      try {
        if (!Number.isInteger(totalPages) || totalPages < 1 || !Number.isInteger(currentPage)
          || currentPage < 1 || currentPage > totalPages || !sectionId) throw new Error('Invalid pagination metadata');
        addCards(galleryEl);
        if (totalPages > 1) {
          root.setAttribute('aria-busy', 'true');
          if (status) { status.hidden = false; status.textContent = 'Loading all artwork…'; }
          const pages = Array.from({ length: totalPages }, (_, index) => index + 1).filter((page) => page !== currentPage);
          let next = 0;
          const worker = async () => { while (next < pages.length) await fetchPage(pages[next++]); };
          await Promise.all([worker(), worker()]);
        }
        if (controller.signal.aborted || !root.isConnected) return;
        root.removeAttribute('aria-busy');
        if (status) status.hidden = true;
        if (pagination) pagination.hidden = true;
        if (cards.size) build([...cards.values()]);
      } catch (error) {
        fail();
        controller.abort();
      }
    }

    function build(list) {
      const sorted = list.sort((a, b) => (a.month === NO_MONTH) - (b.month === NO_MONTH)
        || b.month.localeCompare(a.month) || a.position - b.position || a.id.localeCompare(b.id));
      // Months listed in the section's shuffle setting trade their curated order for a mixed one.
      const shuffleMonths = new Set(values(root.dataset.shuffleMonths));
      const items = [];
      for (let start = 0; start < sorted.length;) {
        let end = start;
        while (end < sorted.length && sorted[end].month === sorted[start].month) end += 1;
        const group = sorted.slice(start, end);
        items.push(...(shuffleMonths.has(group[0].month) ? spreadShuffle(group, group[0].month) : group));
        start = end;
      }
      const uid = `as-${sectionId.replace(/[^\w-]/g, '')}`;
      const months = [...new Set(items.map((card) => card.month))];
      const taggedMonths = new Set(items.filter((card) => FACETS.some((facet) => card[facet].length)).map((card) => card.month));
      const multiYear = new Set(months.filter((month) => month !== NO_MONTH).map((month) => month.slice(0, 4))).size > 1;
      const shortMonth = new Intl.DateTimeFormat('en', multiYear
        ? { month: 'short', year: '2-digit', timeZone: 'UTC' }
        : { month: 'long', timeZone: 'UTC' });
      const monthLabel = (month) => {
        if (month === 'all') return 'All Months';
        return month === NO_MONTH ? 'Other artwork' : longMonth.format(monthDate(month));
      };
      const monthShort = (month) => {
        if (month === 'all') return 'All';
        return month === NO_MONTH ? 'Other' : shortMonth.format(monthDate(month));
      };
      const labelFor = (facet, value) => (facet === 'month' ? monthLabel(value) : LABELS[facet][value] || value);
      const options = {};
      FACETS.forEach((facet) => {
        const counts = new Map();
        items.forEach((card) => card[facet].forEach((value) => counts.set(value, (counts.get(value) || 0) + 1)));
        const hidden = HIDDEN_OPTIONS[facet] || [];
        (ALWAYS_SHOWN[facet] || []).forEach((value) => { if (!counts.has(value)) counts.set(value, 0); });
        // Most common first; "Other" always last.
        options[facet] = [...counts.keys()].filter((value) => !hidden.includes(value)).sort((a, b) =>
          (a === 'another-medium') - (b === 'another-medium') || counts.get(b) - counts.get(a)
          || KNOWN[facet].indexOf(a) - KNOWN[facet].indexOf(b));
      });
      const artistKey = (card) => card.artist.toLowerCase();
      const isTagged = (month) => (month === 'all' ? taggedMonths.size > 0 : taggedMonths.has(month));
      const state = { month: 'all', medium: new Set(), surface: new Set(), sketchbook: new Set() };
      const stateKey = () => JSON.stringify([state.month, ...FACETS.map((facet) => [...state[facet]].sort())]);
      const controls = [];

      /* ---------- Markup ---------- */
      mount.innerHTML = `
        <h2 class="as-sr-only">Filter the artwork</h2>
        <div class="as-panel">
          <div class="as-months-wrap">
            <div class="as-months" role="radiogroup" aria-label="Month"></div>
            <button type="button" class="as-scroll as-scroll--prev is-hidden" tabindex="-1" aria-hidden="true">${ICON.prev}</button>
            <button type="button" class="as-scroll as-scroll--next is-hidden" tabindex="-1" aria-hidden="true">${ICON.next}</button>
          </div>
          <div class="as-summary">
            <button type="button" class="as-filters-button" data-as-open aria-haspopup="dialog">${ICON.sliders}<span>Browse by</span><span class="as-badge" hidden></span></button>
            <p class="as-count" role="status" aria-live="polite" tabindex="-1"></p>
            <div class="as-active"></div>
          </div>
          <div class="as-facets">
            ${FACETS.map((facet) => (DROPDOWNS[facet] ? `
              <div class="as-facet as-facet--${facet}" data-facet="${facet}">
                <label class="as-facet__label" for="${uid}-${facet}-select">${GROUP_TITLES[facet]}</label>
                <div class="as-field" data-select-host></div>
              </div>` : `
              <div class="as-facet as-facet--${facet}" data-facet="${facet}">
                <span class="as-facet__label" id="${uid}-${facet}">${GROUP_TITLES[facet]}</span>
                <div class="as-chips" role="group" aria-labelledby="${uid}-${facet}"></div>
              </div>`)).join('')}
          </div>
        </div>`;

      const bar = document.createElement('div');
      bar.className = 'as-bar';
      bar.setAttribute('aria-hidden', 'true');
      bar.inert = true;
      bar.innerHTML = `
        <div class="page-width as-bar__inner">
          <button type="button" class="as-filters-button" data-as-open aria-haspopup="dialog">${ICON.sliders}<span>Browse by</span><span class="as-badge" hidden></span></button>
          <p class="as-bar__count"></p>
          <div class="as-active as-active--bar"></div>
          <button type="button" class="as-top">${ICON.up}<span>Back to top</span></button>
        </div>`;

      const sheet = document.createElement('dialog');
      sheet.className = 'as-sheet';
      sheet.setAttribute('aria-labelledby', `${uid}-sheet-title`);
      sheet.innerHTML = `
        <div class="as-sheet__head">
          <h2 class="as-sheet__title" id="${uid}-sheet-title">Browse by</h2>
          <button type="button" class="as-sheet__close" aria-label="Close filters">${ICON.close}</button>
        </div>
        <div class="as-sheet__body">
          <div class="as-sheet__group as-sheet__group--month">
            <h3 class="as-sheet__label" id="${uid}-sheet-month">Month</h3>
            <div class="as-months-wrap">
              <div class="as-chips" role="radiogroup" aria-labelledby="${uid}-sheet-month"></div>
              <button type="button" class="as-scroll as-scroll--prev is-hidden" tabindex="-1" aria-hidden="true">${ICON.prev}</button>
              <button type="button" class="as-scroll as-scroll--next is-hidden" tabindex="-1" aria-hidden="true">${ICON.next}</button>
            </div>
          </div>
          ${FACETS.map((facet) => (DROPDOWNS[facet] ? `
            <div class="as-sheet__group" data-facet="${facet}">
              <label class="as-sheet__label" for="${uid}-sheet-${facet}-select">${GROUP_TITLES[facet]}</label>
              <div class="as-field" data-select-host></div>
            </div>` : `
            <div class="as-sheet__group" data-facet="${facet}">
              <h3 class="as-sheet__label" id="${uid}-sheet-${facet}">${GROUP_TITLES[facet]}</h3>
              <div class="as-chips" role="group" aria-labelledby="${uid}-sheet-${facet}"></div>
            </div>`)).join('')}
        </div>
        <div class="as-sheet__foot">
          <button type="button" class="as-clear" data-as-clear>Clear all</button>
          <button type="button" class="as-apply"></button>
        </div>`;
      root.append(bar, sheet);

      const panel = mount.querySelector('.as-panel');
      const countEl = mount.querySelector('.as-count');
      const activeEl = mount.querySelector('.as-active');
      const barCount = bar.querySelector('.as-bar__count');
      const barActive = bar.querySelector('.as-active--bar');
      const applyButton = sheet.querySelector('.as-apply');
      const sheetClear = sheet.querySelector('[data-as-clear]');

      function makeControl(host, facet, value, { tab = false, name } = {}) {
        const type = facet === 'month' ? 'radio' : 'checkbox';
        const wrap = document.createElement('label');
        wrap.className = tab ? 'as-tab' : `as-chip${type === 'radio' ? ' as-chip--radio' : ''}`;
        const input = document.createElement('input');
        input.type = type;
        input.value = value;
        if (name) input.name = name;
        const face = document.createElement('span');
        face.className = tab ? 'as-tab__face' : 'as-chip__face';
        if (tab) {
          face.textContent = monthLabel(value);
        } else {
          const text = document.createElement('span');
          text.textContent = labelFor(facet, value);
          face.append(text);
        }
        wrap.append(input, face);
        input.addEventListener('change', () => {
          if (facet === 'month') {
            state.month = value;
            // Months without tags cannot be narrowed further, so their detail filters are cleared.
            if (!isTagged(value)) FACETS.forEach((key) => state[key].clear());
          } else if (input.checked) {
            state[facet].add(value);
          } else {
            state[facet].delete(value);
          }
          update({ write: true });
        });
        controls.push({ facet, value, input });
        host.append(wrap);
      }

      function makeSelect(host, facet, id) {
        const select = document.createElement('select');
        select.className = 'as-select';
        select.id = id;
        [['', DROPDOWNS[facet]], ...options[facet].map((value) => [value, labelFor(facet, value)])].forEach(([value, text]) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = text;
          select.append(option);
        });
        select.addEventListener('change', () => {
          state[facet] = new Set(select.value ? [select.value] : []);
          update({ write: true });
          // Release a touch-opened native picker so Safari can reopen it on the next tap.
          if (root.dataset.asKeyboard !== 'true') select.blur();
        });
        controls.push({ facet, select });
        host.append(select);
      }

      ['all', ...months].forEach((month) => {
        makeControl(mount.querySelector('.as-months'), 'month', month, { tab: true, name: `${uid}-month` });
        makeControl(sheet.querySelector('[role="radiogroup"]'), 'month', month, { name: `${uid}-sheet-month` });
      });
      FACETS.forEach((facet) => {
        if (!options[facet].length) {
          mount.querySelector(`.as-facet[data-facet="${facet}"]`).remove();
          sheet.querySelector(`.as-sheet__group[data-facet="${facet}"]`).remove();
          return;
        }
        if (DROPDOWNS[facet]) {
          makeSelect(mount.querySelector(`.as-facet[data-facet="${facet}"] [data-select-host]`), facet, `${uid}-${facet}-select`);
          makeSelect(sheet.querySelector(`.as-sheet__group[data-facet="${facet}"] [data-select-host]`), facet, `${uid}-sheet-${facet}-select`);
          return;
        }
        options[facet].forEach((value) => {
          makeControl(mount.querySelector(`.as-facet[data-facet="${facet}"] .as-chips`), facet, value);
          makeControl(sheet.querySelector(`.as-sheet__group[data-facet="${facet}"] .as-chips`), facet, value);
        });
      });

      /* ---------- State ---------- */
      function readUrl() {
        const params = new URL(window.location.href).searchParams;
        const requested = params.get('month');
        if (months.includes(requested)) state.month = requested;
        else state.month = requested === null && root.dataset.defaultView === 'newest' ? months[0] : 'all';
        FACETS.forEach((facet) => {
          const valid = params.getAll(facet).filter((value) => options[facet].includes(value));
          state[facet] = new Set(DROPDOWNS[facet] ? valid.slice(0, 1) : valid);
        });
        if (!isTagged(state.month)) FACETS.forEach((facet) => state[facet].clear());
      }

      function writeUrl() {
        const url = new URL(window.location.href);
        ['medium', 'surface', 'sketchbook', 'month', pageParam].forEach((key) => url.searchParams.delete(key));
        FACETS.forEach((facet) => options[facet].forEach((value) => {
          if (state[facet].has(value)) url.searchParams.append(facet, value);
        }));
        url.searchParams.set('month', state.month);
        // Replace rather than push, so Back leaves the page instead of undoing filters one by one.
        if (url.href !== window.location.href) window.history.replaceState(window.history.state, '', url);
      }

      function passes(card, except) {
        if (except !== 'month' && state.month !== 'all' && card.month !== state.month) return false;
        return FACETS.every((facet) => facet === except || !state[facet].size
          || card[facet].some((value) => state[facet].has(value)));
      }

      function counts() {
        const result = { month: new Map(), medium: new Map(), surface: new Map(), sketchbook: new Map() };
        const bump = (map, key) => map.set(key, (map.get(key) || 0) + 1);
        items.forEach((card) => {
          const matchesFacets = passes(card, 'month');
          if (matchesFacets) bump(result.month, 'all');
          // An untagged month always shows all of its artwork, because choosing it clears the detail filters.
          if (matchesFacets || !taggedMonths.has(card.month)) bump(result.month, card.month);
          FACETS.forEach((facet) => {
            if (passes(card, facet)) card[facet].forEach((value) => bump(result[facet], value));
          });
        });
        return result;
      }

      // The month is a view, not a filter: clearing leaves it as it is.
      function clearAll() {
        FACETS.forEach((facet) => state[facet].clear());
        update({ write: true });
      }

      function activeList() {
        const list = [];
        FACETS.forEach((facet) => options[facet].forEach((value) => {
          if (state[facet].has(value)) list.push([facet, value]);
        }));
        return list;
      }

      function renderActive(host) {
        const focusedIndex = [...host.children].indexOf(document.activeElement);
        host.replaceChildren();
        const list = activeList();
        list.forEach(([facet, value]) => {
          const label = labelFor(facet, value);
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'as-pill-x';
          button.setAttribute('aria-label', `Remove filter: ${label}`);
          button.innerHTML = `<span></span>${ICON.close}`;
          button.firstChild.textContent = label;
          button.addEventListener('click', () => {
            state[facet].delete(value);
            update({ write: true });
          });
          host.append(button);
        });
        if (list.length) {
          const clear = document.createElement('button');
          clear.type = 'button';
          clear.className = 'as-clear';
          clear.textContent = 'Clear all';
          clear.addEventListener('click', clearAll);
          host.append(clear);
        }
        // A removed chip takes focus with it; keep keyboard users in the same row.
        if (focusedIndex !== -1) {
          const next = host.children[Math.min(focusedIndex, host.children.length - 1)];
          (next && next.offsetParent ? next : countEl).focus();
        }
      }

      /* ---------- Gallery ---------- */
      let currentMatching = [];
      let currentColumns = 0;
      const columnsFor = (width) => (width >= 900 ? 3 : 2);
      // Grid spinners run only for cards near the screen, so the many cards further down (whose
      // images have not been asked for yet) cost nothing.
      const spinners = 'IntersectionObserver' in window && new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          target.classList.toggle('is-loading', isIntersecting && !target.querySelector('img').complete);
        });
      }, { rootMargin: '200px 0px' });
      if (spinners) cleanups.push(() => spinners.disconnect());
      items.forEach((card) => {
        const media = card.element.querySelector('.as-card__media');
        const image = card.element.querySelector('img');
        if (!spinners) {
          watchImage(media, image);
          return;
        }
        if (!media || !image || image.complete) return;
        spinners.observe(media);
        const done = () => {
          spinners.unobserve(media);
          media.classList.remove('is-loading');
        };
        image.addEventListener('load', done, { once: true });
        image.addEventListener('error', done, { once: true });
      });

      function renderGallery(matching) {
        currentMatching = matching;
        galleryEl.replaceChildren();
        if (!matching.length) {
          const empty = document.createElement('div');
          empty.className = 'as-empty';
          empty.innerHTML = '<p>No artwork matches these filters.</p>';
          const clear = document.createElement('button');
          clear.type = 'button';
          clear.className = 'as-filters-button';
          clear.textContent = 'Clear all filters';
          clear.addEventListener('click', clearAll);
          empty.append(clear);
          galleryEl.append(empty);
          return;
        }
        const width = galleryEl.clientWidth || root.clientWidth;
        const columns = columnsFor(width);
        currentColumns = columns;
        const gap = width < 750 ? 12 : 24;
        const columnWidth = Math.max(1, (width - gap * (columns - 1)) / columns);
        // Caption and row gap, expressed in image-width units so they weigh in on column height.
        const extra = (width < 750 ? 52 : 66) / columnWidth;
        const groups = new Map();
        matching.forEach((card) => {
          if (!groups.has(card.month)) groups.set(card.month, []);
          groups.get(card.month).push(card);
        });
        groups.forEach((list, month) => {
          const section = document.createElement('section');
          section.className = 'as-month';
          section.dataset.month = month;
          const head = document.createElement('div');
          head.className = 'as-month__head';
          const title = document.createElement('h2');
          title.className = 'as-month__title';
          title.textContent = monthLabel(month);
          head.append(title);
          const row = document.createElement('div');
          row.className = 'as-cols';
          const cols = Array.from({ length: columns }, () => {
            const col = document.createElement('div');
            col.className = 'as-col';
            row.append(col);
            return col;
          });
          const heights = new Array(columns).fill(0);
          // Shortest-column placement keeps the curated order reading left to right.
          list.forEach((card) => {
            let target = 0;
            for (let index = 1; index < columns; index += 1) if (heights[index] < heights[target]) target = index;
            cols[target].append(card.element);
            heights[target] += card.ratio + extra;
          });
          section.append(head, row);
          galleryEl.append(section);
        });
      }

      /* ---------- Update cycle ---------- */
      function update({ write = false } = {}) {
        const result = counts();
        controls.forEach(({ facet, value, input, select }) => {
          if (select) {
            // Dropdown choices are never disabled; an empty result shows the empty state instead.
            select.value = [...state[facet]][0] || '';
            select.classList.toggle('is-active', Boolean(select.value));
            return;
          }
          const checked = facet === 'month' ? state.month === value : state[facet].has(value);
          input.checked = checked;
          input.disabled = !checked && !result[facet].get(value);
        });

        const tagged = isTagged(state.month);
        panel.classList.toggle('is-untagged', !tagged);
        sheet.querySelectorAll('.as-sheet__group[data-facet]').forEach((group) => { group.hidden = !tagged; });

        const matching = items.filter((card) => passes(card, null));
        const selections = FACETS.reduce((total, facet) => total + state[facet].size, 0);
        const inMonth = state.month === 'all' ? items : items.filter((card) => card.month === state.month);
        const artists = new Set(matching.map(artistKey)).size;
        countEl.innerHTML = selections
          ? `Showing <strong>${matching.length}</strong> of ${plural(inMonth.length, 'artwork')}<span class="as-count__artists"> · ${plural(artists, 'artist')}</span>`
          : `<strong>${inMonth.length}</strong> ${inMonth.length === 1 ? 'artwork' : 'artworks'}<span class="as-count__artists"> from ${plural(artists, 'artist')}</span>`;
        barCount.innerHTML = `<strong>${matching.length}</strong> ${matching.length === 1 ? 'artwork' : 'artworks'}`
          + (state.month === 'all' ? '' : ` · ${monthShort(state.month)}`);
        renderActive(activeEl);
        renderActive(barActive);
        root.querySelectorAll('.as-badge').forEach((badge) => {
          badge.hidden = !selections;
          badge.textContent = selections;
        });
        root.querySelectorAll('[data-as-open]').forEach((button) => {
          button.setAttribute('aria-label', selections ? `Browse by, ${selections} selected` : 'Browse by');
          // A month without tags has nothing to filter, so its Filters buttons go, as the desktop rows do.
          button.hidden = !tagged;
        });
        sheetClear.disabled = !selections;
        applyButton.textContent = `Show ${plural(matching.length, 'artwork')}`;

        if (hint) hint.hidden = !matching.length;
        renderGallery(matching);
        revealActiveMonth();
        updateArrows();
        if (write) writeUrl();
        scheduleMeasure();
      }

      /* ---------- Sticky bar ---------- */
      let frame = 0;
      let lastActivity = 0;
      function measure() {
        frame = 0;
        const top = headerBottom();
        const panelBox = panel.getBoundingClientRect();
        const galleryBox = galleryEl.getBoundingClientRect();
        const visible = panelBox.bottom < top + 4 && galleryBox.bottom > top + 160;
        bar.style.top = `${Math.round(top)}px`;
        if (visible !== bar.classList.contains('is-visible')) {
          bar.classList.toggle('is-visible', visible);
          bar.inert = !visible;
          bar.setAttribute('aria-hidden', String(!visible));
        }
        // Dawn slides its header in and out after scrolling stops; follow it for a moment.
        if (performance.now() - lastActivity < 400) frame = requestAnimationFrame(measure);
      }
      function scheduleMeasure() {
        lastActivity = performance.now();
        if (!frame) frame = requestAnimationFrame(measure);
      }
      listen(window, 'scroll', scheduleMeasure, { passive: true });
      listen(window, 'resize', scheduleMeasure);
      cleanups.push(() => cancelAnimationFrame(frame));

      const scrollToPanel = () => {
        const header = document.querySelector('.section-header');
        const offset = (header ? header.offsetHeight : 0) + 16;
        const top = panel.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion() ? 'auto' : 'smooth' });
      };
      bar.querySelector('.as-top').addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: reduceMotion() ? 'auto' : 'smooth' });
      });

      /* ---------- Drawer / bottom sheet ---------- */
      let keyAtOpen = '';
      const lockScroll = (locked) => {
        document.documentElement.style.overflow = locked && window.innerWidth < 750 ? 'hidden' : '';
      };
      cleanups.push(() => lockScroll(false));
      root.querySelectorAll('[data-as-open]').forEach((button) => button.addEventListener('click', () => {
        keyAtOpen = stateKey();
        // Opened from the sticky bar, the month pills are out of view, so the sheet shows them.
        sheet.dataset.from = button.closest('.as-bar') ? 'bar' : 'page';
        sheet.showModal();
        lockScroll(true);
        revealActiveMonth();
        updateArrows();
      }));
      sheet.querySelector('.as-sheet__close').addEventListener('click', () => sheet.close());
      applyButton.addEventListener('click', () => sheet.close());
      sheetClear.addEventListener('click', clearAll);
      sheet.addEventListener('click', (event) => { if (event.target === sheet) sheet.close(); });
      sheet.addEventListener('close', () => {
        lockScroll(false);
        // Filters changed from deep in the gallery: bring the fresh results into view.
        if (stateKey() !== keyAtOpen && panel.getBoundingClientRect().top < headerBottom()) scrollToPanel();
      });

      // The month rows on the page and in the phone sheet scroll sideways under arrow buttons;
      // each arrow goes once there is nothing further that way.
      const monthRows = [mount, sheet].map((host) => {
        const strip = host.querySelector('.as-months-wrap [role="radiogroup"]');
        const previous = host.querySelector('.as-scroll--prev');
        const next = host.querySelector('.as-scroll--next');
        const refresh = () => {
          const end = strip.scrollWidth - strip.clientWidth;
          previous.classList.toggle('is-hidden', strip.scrollLeft <= 1);
          next.classList.toggle('is-hidden', strip.scrollLeft >= end - 1);
        };
        [[previous, -1], [next, 1]].forEach(([button, direction]) => button.addEventListener('click', () => {
          strip.scrollBy({ left: direction * strip.clientWidth * 0.7, behavior: reduceMotion() ? 'auto' : 'smooth' });
        }));
        strip.addEventListener('scroll', refresh, { passive: true });
        return { strip, refresh };
      });
      function updateArrows() {
        monthRows.forEach((row) => row.refresh());
      }
      function revealActiveMonth() {
        monthRows.forEach(({ strip }) => {
          const active = strip.querySelector('input:checked')?.closest('label');
          if (!active || strip.scrollWidth <= strip.clientWidth) return;
          const outer = strip.getBoundingClientRect();
          const box = active.getBoundingClientRect();
          if (box.left < outer.left || box.right > outer.right) strip.scrollLeft += box.left - outer.left - 16;
        });
      }
      listen(window, 'resize', updateArrows);
      // Pill widths settle once the body font arrives.
      document.fonts?.ready.then(() => { if (!instance.destroyed) updateArrows(); });

      /* ---------- Lightbox ---------- */
      const lightbox = document.createElement('dialog');
      lightbox.className = 'as-lightbox';
      lightbox.tabIndex = -1;
      lightbox.setAttribute('aria-label', 'Artwork viewer');
      lightbox.innerHTML = `
        <div class="as-lightbox__stage"><div class="as-lightbox__track">${'<div class="as-lightbox__slide"></div>'.repeat(3)}</div></div>
        <button type="button" class="as-lightbox__close" aria-label="Close">${ICON.close}</button>
        <button type="button" class="as-lightbox__nav as-lightbox__nav--prev" aria-label="Previous artwork">${ICON.prev}</button>
        <button type="button" class="as-lightbox__nav as-lightbox__nav--next" aria-label="Next artwork">${ICON.next}</button>
        <div class="as-lightbox__caption">
          <a class="as-lightbox__credit" target="_blank" rel="noopener noreferrer"><span class="as-lightbox__artist"></span><span class="as-lightbox__ig">${ICON.instagram}<span></span></span></a>
          <span class="as-lightbox__position" aria-live="polite"></span>
        </div>`;
      root.append(lightbox);
      const stage = lightbox.querySelector('.as-lightbox__stage');
      const track = lightbox.querySelector('.as-lightbox__track');
      const lightboxCredit = lightbox.querySelector('.as-lightbox__credit');
      const lightboxArtist = lightbox.querySelector('.as-lightbox__artist');
      const lightboxHandle = lightbox.querySelector('.as-lightbox__ig > span');
      const lightboxPosition = lightbox.querySelector('.as-lightbox__position');
      const previousButton = lightbox.querySelector('.as-lightbox__nav--prev');
      const nextButton = lightbox.querySelector('.as-lightbox__nav--next');
      const cardByElement = new Map(items.map((card) => [card.element, card]));
      const lightboxSizes = '(min-width: 750px) calc(100vw - 192px), 100vw';
      // Three slides: previous, current and next. The neighbours load ahead and sit just off-screen.
      let slides = [...track.children];
      let sequence = [];
      let current = 0;
      let finishMove = null;

      // Each artwork gets a fresh image, so a reused slide never shows the previous one while
      // the new one loads; a spinner holds its place meanwhile.
      function fillSlide(slide, card) {
        if (slide.card === card) return;
        slide.card = card;
        slide.replaceChildren();
        if (!card) return;
        const source = card.element.querySelector('img');
        const image = document.createElement('img');
        image.className = 'as-lightbox__img';
        image.decoding = 'async';
        // Decoded as soon as it arrives, so a neighbour is ready to paint when it slides in.
        image.addEventListener('load', () => image.decode?.().catch(() => {}), { once: true });
        image.draggable = false;
        image.alt = source.alt;
        image.sizes = lightboxSizes;
        image.srcset = source.getAttribute('srcset') || '';
        image.src = source.getAttribute('src') || '';
        slide.append(image);
        watchImage(slide, image);
      }

      function showCaption() {
        const card = sequence[current];
        lightboxArtist.textContent = card.artist;
        // With an Instagram account, the name, icon and handle form one link.
        const instagram = card.element.querySelector('.as-card__ig');
        if (instagram) {
          lightboxCredit.href = instagram.href;
          lightboxCredit.setAttribute('aria-label', `${card.artist} on Instagram (opens in a new tab)`);
          lightboxHandle.textContent = `@${decodeURIComponent(new URL(instagram.href).pathname.replace(/\//g, ''))}`;
        } else {
          lightboxCredit.removeAttribute('href');
          lightboxCredit.removeAttribute('aria-label');
        }
        lightboxPosition.textContent = `${current + 1} of ${sequence.length}`;
        previousButton.disabled = current === 0;
        nextButton.disabled = current === sequence.length - 1;
      }

      function renderSlides() {
        [-1, 0, 1].forEach((offset, index) => {
          fillSlide(slides[index], sequence[current + offset]);
          slides[index].classList.toggle('is-current', offset === 0);
          slides[index].setAttribute('aria-hidden', String(offset !== 0));
        });
        track.style.transition = '';
        track.style.transform = '';
      }

      function showArtwork(index) {
        if (finishMove) finishMove();
        current = Math.max(0, Math.min(index, sequence.length - 1));
        showCaption();
        renderSlides();
      }

      // One slide step in pixels: the track's width plus the gap between slides.
      const slideStep = () => track.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).columnGap) || 0);
      const placeTrack = (offset) => {
        track.style.transform = `translate3d(${offset - slideStep()}px, 0, 0)`;
      };

      // Slides the track from one offset to another, then runs done (also when interrupted).
      function moveTrack(from, to, done) {
        if (finishMove) finishMove();
        if (reduceMotion()) {
          done();
          return;
        }
        track.style.transition = 'none';
        placeTrack(from);
        track.getBoundingClientRect();
        track.style.transition = 'transform 0.34s cubic-bezier(0.22, 0.8, 0.3, 1)';
        placeTrack(to);
        let timer = 0;
        const finish = () => {
          if (finishMove !== finish) return;
          finishMove = null;
          window.clearTimeout(timer);
          track.removeEventListener('transitionend', onEnd);
          done();
        };
        const onEnd = (event) => { if (event.target === track) finish(); };
        finishMove = finish;
        track.addEventListener('transitionend', onEnd);
        timer = window.setTimeout(finish, 480);
      }

      // Moves to the neighbouring artwork, sliding on from where a drag left the track.
      function step(direction, from = 0) {
        if (finishMove) finishMove();
        const target = current + direction;
        if (target < 0 || target >= sequence.length) {
          if (from) moveTrack(from, 0, renderSlides);
          return;
        }
        current = target;
        showCaption();
        moveTrack(from, -direction * slideStep(), () => {
          // The incoming slide becomes the middle one; the far slide is reused for the next neighbour.
          if (direction > 0) slides.push(slides.shift());
          else slides.unshift(slides.pop());
          track.replaceChildren(...slides);
          renderSlides();
        });
      }

      function openLightbox(card) {
        sequence = currentMatching.slice();
        const index = sequence.indexOf(card);
        if (index === -1) return;
        showArtwork(index);
        lightbox.showModal();
        // Focus the viewer itself so a tap does not leave a focus ring on its first button.
        lightbox.focus();
        document.documentElement.style.overflow = 'hidden';
      }

      galleryEl.addEventListener('click', (event) => {
        const link = event.target.closest('.as-card__media');
        if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const card = cardByElement.get(link.closest('.as-card'));
        if (!card) return;
        event.preventDefault();
        openLightbox(card);
      });
      previousButton.addEventListener('click', () => step(-1));
      nextButton.addEventListener('click', () => step(1));
      lightbox.querySelector('.as-lightbox__close').addEventListener('click', () => lightbox.close());
      lightbox.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          step(event.key === 'ArrowRight' ? 1 : -1);
        }
      });
      lightbox.addEventListener('close', () => {
        document.documentElement.style.overflow = '';
        if (finishMove) finishMove();
        drag = null;
        slides.forEach((slide) => fillSlide(slide, null));
        track.style.transform = '';
      });
      cleanups.push(() => { document.documentElement.style.overflow = ''; });

      // Touch drags move the artwork with the finger; letting go past a fifth of the width (or
      // with a quick flick) slides on to the neighbour, otherwise it springs back. A tap on the
      // left or right third slides back or on; elsewhere, a tap or click on the dark area closes.
      let drag = null;
      let swiped = false;
      let tapPointer = 'mouse';
      function dragTo(event) {
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;
        if (!drag.active) {
          if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy)) return;
          drag.active = true;
          swiped = true;
          track.style.transition = 'none';
        }
        const elapsed = event.timeStamp - drag.time;
        if (elapsed > 0) drag.velocity = (event.clientX - drag.lastX) / elapsed;
        drag.lastX = event.clientX;
        drag.time = event.timeStamp;
        // Past the first or last artwork the track resists, so it reads as an edge.
        const atEdge = (dx > 0 && current === 0) || (dx < 0 && current === sequence.length - 1);
        drag.offset = atEdge ? dx / 3 : dx;
        placeTrack(drag.offset);
      }
      stage.addEventListener('pointerdown', (event) => {
        swiped = false;
        tapPointer = event.pointerType;
        if (event.pointerType === 'mouse' || !event.isPrimary) return;
        if (finishMove) finishMove();
        drag = { id: event.pointerId, x: event.clientX, y: event.clientY, lastX: event.clientX, time: event.timeStamp, offset: 0, velocity: 0, active: false };
      });
      stage.addEventListener('pointermove', (event) => {
        if (drag && event.pointerId === drag.id) dragTo(event);
      });
      stage.addEventListener('pointerup', (event) => {
        if (!drag || event.pointerId !== drag.id) return;
        dragTo(event);
        const { active, offset, velocity } = drag;
        drag = null;
        if (!active) return;
        const flick = Math.abs(velocity) > 0.35 && Math.sign(velocity) === Math.sign(offset) && Math.abs(offset) > 24;
        if (Math.abs(offset) > slideStep() * 0.2 || flick) step(offset < 0 ? 1 : -1, offset);
        else moveTrack(offset, 0, renderSlides);
      });
      stage.addEventListener('pointercancel', (event) => {
        if (!drag || event.pointerId !== drag.id) return;
        const { active, offset } = drag;
        drag = null;
        if (active) moveTrack(offset, 0, renderSlides);
      });
      stage.addEventListener('click', (event) => {
        if (swiped) return;
        if (tapPointer !== 'mouse') {
          const box = stage.getBoundingClientRect();
          const side = (event.clientX - box.left) / box.width;
          if (side < 1 / 3 || side > 2 / 3) {
            step(side < 1 / 3 ? -1 : 1);
            return;
          }
        }
        if (!event.target.closest('.as-lightbox__img')) lightbox.close();
      });

      /* ---------- Resize and history ---------- */
      let resizeTimer = 0;
      listen(window, 'resize', () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => {
          if (columnsFor(galleryEl.clientWidth) !== currentColumns) renderGallery(currentMatching);
        }, 150);
      });
      cleanups.push(() => window.clearTimeout(resizeTimer));
      listen(document, 'pointerdown', () => { root.dataset.asKeyboard = 'false'; }, true);
      listen(document, 'keydown', (event) => {
        if (!event.altKey && !event.ctrlKey && !event.metaKey) root.dataset.asKeyboard = 'true';
      }, true);
      listen(window, 'popstate', () => {
        readUrl();
        update();
      });

      readUrl();
      root.classList.add('is-ready');
      update();
      // The first row is what people see first, so its images skip the lazy-load wait and go
      // ahead of other downloads.
      currentMatching.slice(0, currentColumns).forEach((card) => {
        const image = card.element.querySelector('img');
        image.fetchPriority = 'high';
        image.loading = 'eager';
      });
    }

    // Further pages are requested right away: waiting for the window load event held the whole
    // gallery behind every app script on the page.
    load();
  }

  function scan() {
    document.querySelectorAll('[data-artwork-showcase]').forEach(init);
  }

  window[globalName] = { scan };
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-artwork-showcase]').forEach((root) => root.artworkShowcase?.destroy());
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true });
  else scan();
})();
